import {
  StreamingCameraType,
  CameraAlertDetection,
  CameraAlertSchedule,
  CameraAlertNotifer,
  AdvancedAISettingType,
} from "lib/types";
import { ReactNode, createContext, useEffect, useState } from "react";
import _ from "lodash";

type AutomationContextType = {
  showMask: boolean;
  setShowMask: (newState: boolean) => void;

  streamingCamera: StreamingCameraType | null;
  setStreamingCamera: (newState: StreamingCameraType) => void;

  cameraAlertDetections: CameraAlertDetection[];
  setCameraAlertDetections: (newState: CameraAlertDetection[]) => void;

  crossLineDetections: CameraAlertDetection[];
  setCrossLineDetections: (newState: CameraAlertDetection[]) => void;

  supportedEventTypes: string[];
  setSupportedEventTypes: (newState: string[]) => void;
  note: {
    note: string;
    types: string[];
  };
  setNote: (newState: { note: string; types: string[] }) => void;
  alertSchedules: CameraAlertSchedule[];
  setAlertSchedules: (newState: CameraAlertSchedule[]) => void;

  actions: { notifiers: CameraAlertNotifer }[];
  setActions: (newState: { notifiers: CameraAlertNotifer }[]) => void;

  reseteAvailable: boolean;
  setReseteAvailable: (newState: boolean) => void;

  advancedAISetting: AdvancedAISettingType;
  setAdvancedAISetting: (newState: AdvancedAISettingType) => void;

  setResetedData: (newState: any) => void;
  reset: () => void;
};

const initialValue = {
  showMask: false,
  setShowMask: () => {},
  streamingCamera: null,
  setStreamingCamera: () => {},
  cameraAlertDetections: [],
  setCameraAlertDetections: () => {},
  crossLineDetections: [],
  setCrossLineDetections: () => {},
  supportedEventTypes: [],
  setSupportedEventTypes: () => {},
  alertSchedules: [],
  setAlertSchedules: () => {},
  note: {
    note: "",
    types: [],
  },
  setNote: () => {},
  actions: [],
  setActions: () => {},

  reseteAvailable: false,
  setReseteAvailable: () => {},

  setResetedData: () => {},
  advancedAISetting: null,
  setAdvancedAISetting: () => {},

  reset: () => {},
};

initialValue.cameraAlertDetections.push({
  objects: [
    {
      minDetectionHeight: 0,
      minDetectionWidth: 0,
      type: "person",
    },
  ],
});
initialValue.crossLineDetections.push({
  objects: [
    {
      minDetectionHeight: 0,
      minDetectionWidth: 0,
      type: "person",
    },
  ],
});

export const AutomationContext =
  createContext<AutomationContextType>(initialValue);

export default function AutomationProvider({
  children,
}: {
  children?: ReactNode;
}) {
  const [showMask, setShowMask] = useState(initialValue.showMask);
  const [streamingCamera, setStreamingCamera] = useState(
    initialValue.streamingCamera,
  );
  const [cameraAlertDetections, setCameraAlertDetections] = useState(
    initialValue.cameraAlertDetections,
  );
  const [crossLineDetections, setCrossLineDetections] = useState(
    initialValue.crossLineDetections,
  );
  const [supportedEventTypes, setSupportedEventTypes] = useState(
    initialValue.supportedEventTypes,
  );

  const [alertSchedules, setAlertSchedules] = useState(
    initialValue.alertSchedules,
  );
  const [note, setNote] = useState(initialValue.note);
  const [actions, setActions] = useState(initialValue.actions);

  const [advancedAISetting, setAdvancedAISetting] =
    useState<AdvancedAISettingType>(null);

  const [resetedData, setResetedData] = useState({
    alertSchedules: [],
    algorithms: [],
    actions: [],
    note: {
      note: "",
      types: [],
    },
    advancedAISetting: null,
  });

  const [reseteAvailable, setReseteAvailable] = useState(false);

  const reset = () => {
    if (!resetedData) {
      return;
    }
    const copyData = _.cloneDeep(resetedData);
    setAlertSchedules(copyData.alertSchedules);
    setNote(copyData.note);
    setCameraAlertDetections(
      copyData.algorithms?.find((alg) => alg?.algo === "intrusion")
        ?.detections || [],
    );
    setCrossLineDetections(
      copyData.algorithms?.find((alg) => alg?.algo === "cross_line")
        ?.detections || [],
    );
    setActions(copyData.actions);
    setAdvancedAISetting(copyData.advancedAISetting);
  };

  useEffect(() => {
    reset();
  }, [resetedData]);

  useEffect(() => {
    if (
      _.isEqual(resetedData.alertSchedules, alertSchedules) &&
      _.isEqual(
        resetedData.algorithms?.find((alg) => alg.algo === "intrusion")
          ?.detections || [],
        cameraAlertDetections,
      ) &&
      _.isEqual(
        resetedData.algorithms?.find((alg) => alg.algo === "cross_line")
          ?.detections || [],
        crossLineDetections,
      ) &&
      _.isEqual(resetedData.actions, actions) &&
      _.isEqual(resetedData.note, note) &&
      _.isEqual(resetedData.advancedAISetting, advancedAISetting)
    ) {
      setReseteAvailable(false);
    } else {
      setReseteAvailable(true);
    }
  }, [
    cameraAlertDetections,
    crossLineDetections,
    alertSchedules,
    actions,
    note,
    advancedAISetting,
  ]);

  return (
    <AutomationContext.Provider
      value={{
        showMask,
        setShowMask,
        streamingCamera,
        setStreamingCamera,
        cameraAlertDetections,
        setCameraAlertDetections,
        crossLineDetections,
        setCrossLineDetections,
        supportedEventTypes,
        setSupportedEventTypes,
        alertSchedules,
        setAlertSchedules,
        advancedAISetting,
        setAdvancedAISetting,
        note,
        setNote,
        actions,
        setActions,
        setResetedData,
        reseteAvailable,
        setReseteAvailable,
        reset,
      }}
    >
      {children}
    </AutomationContext.Provider>
  );
}
